import { COLORS } from '@avalara/bat-apps-shared/constants';
import { t } from '@avalara/bat-apps-shared/i18n';
import { SBox, SCol, SIcon, SRow } from '@avalara/skylab-sdk/react';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import type { QuestionType } from '../../../../../types/prequal-questionnaire';

import { selectState } from '../../../selectors';
import { selectAnswer } from '../../../store/prequal-questionnaire';

const FakeLabel = styled.label`
  margin-top: -1rem !important;
`;

const QuestionWrapper = styled.div<{
  selected: boolean;
}>`
cursor: pointer;
width: 40%;
margin-right: 1.5rem;
${({ selected }) =>
  selected &&
  `
  s-box {
    border: 1px ${COLORS.blue} solid;
  }
`}`;

type NexusThresholdsQuestionProps = {
  questionData: QuestionType;
  questionSelectedAnswers: Array<string>;
};

// 50,64,111-130
const NexusThresholdsQuestion = ({ questionData, questionSelectedAnswers }: NexusThresholdsQuestionProps) => {
  const { economicNexus } = useSelector(selectState);
  const dispatch = useDispatch();

  const {
    id: questionId,
    inputData: { inputType: questionType },
    questionTitle,
    optionsHeader,
    subText,
    subTextTooltipUrl,
    otherSubtext,
  } = questionData;

  const groupName = `${questionId}Group`;
  const tooltipButtonId = 'sub-text-tooltip-button';

  const onSelect = (answer: string) => {
    dispatch(selectAnswer({ questionId, questionType, answer }));
  };

  const openTooltip = () => {
    if (subTextTooltipUrl) window.iph?.Popover(tooltipButtonId, subTextTooltipUrl).open();
  };

  // NOTE: Can only be approaching question or exceeding question so only checking for one of them
  const isExceedingQuestion = questionId === 'exceedingNexusThresholds';
  const states = isExceedingQuestion ? economicNexus.exceedingThreshold : economicNexus.approachingThreshold;

  // biome-ignore lint/correctness/useExhaustiveDependencies: leaving as is
  useEffect(() => {
    // NOTE: Need to default all states to selected if exceeding threshold question
    if (!questionSelectedAnswers.length && isExceedingQuestion) {
      for (const state of states) {
        dispatch(selectAnswer({ questionId, questionType, answer: state }));
      }
    }
  }, []);

  return (
    <>
      <SRow className='flex flex-dir-col'>
        <SCol className='text-left pad-bottom-none'>
          <h1 data-ref='buy.prequal.questions.nexus-threshold.header'>{questionTitle}</h1>

          <p data-ref='buy.prequal.questions.nexus-threshold.subText' className='margin-bottom-sm'>
            {subText}
            {!!subTextTooltipUrl && (
              <button
                type='button'
                data-ref='buy.prequal.questions.nexus-threshold.button'
                id={tooltipButtonId}
                aria-label={t('client.accessibility.open-tooltip-with-more-info')}
                className='icon ghost-blue'
                onClick={openTooltip}
              >
                <SIcon name='info-circle' />
              </button>
            )}
            {otherSubtext}
          </p>

          <p className='text-xs-strong margin-bottom-sm'>{optionsHeader}</p>
        </SCol>
      </SRow>

      <p data-ref='buy.prequal.questions.nexus-threshold.selected-states-text' className='text-xs margin-left-sm'>
        {t('client.general.selected-count-of-total-states-selected', {
          selected: questionSelectedAnswers.length,
          total: states.length,
        })}
      </p>

      <hr className='margin-bottom-lg margin-left-sm margin-right-sm' />

      <SRow>
        <fieldset
          className='flex flex-dir-row flex-wrap margin-left-sm margin-right-sm'
          style={{ width: '100%', maxHeight: '450px', overflowY: 'auto' }}
        >
          {states.map((state, index) => (
            <QuestionWrapper
              data-ref='buy.prequal.questions.nexus-threshold.select-button'
              onClick={() => onSelect(state)}
              className='padding-bottom-sm margin-bottom-sm'
              selected={questionSelectedAnswers.includes(state)}
              key={state}
            >
              <SBox className={questionSelectedAnswers.includes(state) ? 'secondary' : ''}>
                <div className='flex align-items-center'>
                  <input
                    data-ref='buy.prequal.questions.nexus-threshold.input'
                    type={questionType}
                    id={`${groupName}${index}Input`}
                    aria-labelledby={`${groupName}${index}Label`}
                    value={state}
                    checked={questionSelectedAnswers.includes(state)}
                    onChange={() => {}}
                    name={groupName}
                  />

                  {/* Fake label to get skylab input to render */}
                  <FakeLabel onClick={(e) => e.stopPropagation()} htmlFor={`${groupName}${index}Input`} />

                  <span id={`${groupName}${index}Label`} className='margin-all-sm font-semibold text-capitalize'>
                    {state}
                  </span>
                </div>
              </SBox>
            </QuestionWrapper>
          ))}
        </fieldset>
      </SRow>

      <hr className='margin-top-lg margin-bottom-lg margin-left-sm margin-right-sm' />
    </>
  );
};

export default NexusThresholdsQuestion;
